import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePlaidLink } from 'react-plaid-link'
import type { Account, AccountType } from '@/features/transactions/types'
import { useAccounts } from '@/features/transactions/hooks'
import {
  createLinkToken,
  exchangePublicToken,
  refreshAllPlaidItems,
} from './plaid-link'
import { ReconcileDialog } from './ReconcileDialog'

/**
 * Accounts screen — list grouped by type, link new banks via Plaid
 * Link for Web, refresh sync via the backend's bulk-refresh endpoint.
 *
 * MVP scoping decisions:
 *  - No OAuth-bank flow yet (see plaid-link.ts comment). Non-OAuth
 *    institutions and the sandbox work today.
 *  - No manual accounts yet (WEB-28 follow-up).
 *  - Account detail page (per-account txn filter) deferred to WEB-27.
 *  - Institution logo render — we already store it server-side
 *    (`PlaidItemDoc.institutionLogo`); a follow-up will surface it
 *    here. For now we show the type icon.
 */

const TYPE_GROUPS: Array<{ label: string; types: AccountType[] }> = [
  { label: 'Budget accounts', types: ['checking', 'savings', 'cash'] },
  { label: 'Credit cards', types: ['credit_card'] },
  { label: 'Tracking & investments', types: ['tracking', 'investment', 'other_asset'] },
  { label: 'Loans & mortgages', types: ['mortgage', 'loan', 'other_liability'] },
]

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function AccountsScreen() {
  const accountsQuery = useAccounts()
  const qc = useQueryClient()

  // Lazy: only mint a link_token when the user clicks "Add bank".
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [reconcileFor, setReconcileFor] = useState<Account | null>(null)

  const exchange = useMutation({
    mutationFn: async (publicToken: string) => exchangePublicToken(publicToken),
    onSuccess: () => {
      sessionStorage.removeItem('crawfish.budget.plaid.linkToken')
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['plaid_items'] })
    },
    onError: (err) => setLinkError(err instanceof Error ? err.message : 'Exchange failed'),
  })

  const refresh = useMutation({
    mutationFn: refreshAllPlaidItems,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })

  const plaid = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken) => {
      exchange.mutate(publicToken)
      setLinkToken(null)
    },
    onExit: (err) => {
      if (err) setLinkError(err.error_message ?? err.error_code ?? 'Link cancelled')
      setLinkToken(null)
    },
  })

  // Auto-open Link once the token is ready + the SDK has loaded the
  // iframe. usePlaidLink.ready flips true when Plaid's script is in.
  useEffect(() => {
    if (linkToken && plaid.ready) plaid.open()
  }, [linkToken, plaid])

  async function startLink() {
    setLinkError(null)
    try {
      const resp = await createLinkToken()
      // Persist for the OAuth resume path. If the user picks an OAuth
      // bank (BofA, Chase, etc.) Plaid will redirect the whole window
      // away from this page and back to /plaid-oauth. That page reads
      // this token from sessionStorage to resume the same link
      // session. sessionStorage is scoped to the tab so a stale token
      // from a previous tab can't interfere.
      sessionStorage.setItem('crawfish.budget.plaid.linkToken', resp.linkToken)
      setLinkToken(resp.linkToken)
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to start linking')
    }
  }

  const grouped = useMemo(() => groupAccounts(accountsQuery.data ?? []), [accountsQuery.data])

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Accounts</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="rounded-md border border-zinc-300 px-3 py-1 text-sm hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            {refresh.isPending ? 'Refreshing…' : 'Refresh all'}
          </button>
          <button
            type="button"
            onClick={startLink}
            disabled={exchange.isPending || linkToken !== null}
            className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {exchange.isPending ? 'Linking…' : '+ Add bank'}
          </button>
        </div>
      </header>

      {linkError && (
        <p role="alert" className="rounded-md bg-overspent-subtle p-3 text-sm text-overspent">
          {linkError}
        </p>
      )}

      {accountsQuery.isLoading && (
        <div className="rounded-xl border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
          Loading…
        </div>
      )}

      {accountsQuery.isSuccess && (accountsQuery.data?.length ?? 0) === 0 && (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No accounts yet. Click <strong>Add bank</strong> to link your first one.
        </div>
      )}

      <div className="space-y-6">
        {TYPE_GROUPS.map((g) => {
          const accounts = grouped.get(g.label)
          if (!accounts || accounts.length === 0) return null
          return (
            <GroupSection
              key={g.label}
              label={g.label}
              accounts={accounts}
              onReconcile={setReconcileFor}
            />
          )
        })}
      </div>

      {reconcileFor && (
        <ReconcileDialog account={reconcileFor} onClose={() => setReconcileFor(null)} />
      )}
    </div>
  )
}

function groupAccounts(accounts: Account[]): Map<string, Account[]> {
  const out = new Map<string, Account[]>()
  for (const a of accounts.filter((acc) => !acc.isHidden)) {
    const g = TYPE_GROUPS.find((tg) => tg.types.includes(a.type))?.label ?? 'Other'
    const existing = out.get(g) ?? []
    existing.push(a)
    out.set(g, existing)
  }
  for (const list of out.values()) list.sort((a, b) => a.sortOrder - b.sortOrder)
  return out
}

function GroupSection({
  label,
  accounts,
  onReconcile,
}: {
  label: string
  accounts: Account[]
  onReconcile: (a: Account) => void
}) {
  const total = accounts.reduce((s, a) => s + a.workingBalance, 0)
  return (
    <section className="rounded-xl border border-zinc-200 dark:border-zinc-800">
      <header className="flex items-center justify-between bg-zinc-50 px-4 py-2 text-sm font-medium dark:bg-zinc-900">
        <span>{label}</span>
        <span className="tabular-nums text-zinc-500">{usd.format(total)}</span>
      </header>
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {accounts.map((a) => (
          <li key={a.id} className="group flex items-center justify-between gap-3 px-4 py-3 text-sm">
            <div>
              <div className="font-medium">{a.name}</div>
              <div className="text-xs text-zinc-500">{labelForType(a.type)}</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onReconcile(a)}
                className="rounded-md border border-zinc-300 px-2 py-1 text-xs opacity-0 transition-opacity hover:bg-zinc-100 focus:opacity-100 group-hover:opacity-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
              >
                Reconcile
              </button>
              <div className="text-right tabular-nums">
                <div className="font-medium">{usd.format(a.workingBalance)}</div>
                {a.unclearedBalance !== 0 && (
                  <div className="text-xs text-zinc-500">
                    {usd.format(a.unclearedBalance)} pending
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function labelForType(t: AccountType): string {
  switch (t) {
    case 'checking':
      return 'Checking'
    case 'savings':
      return 'Savings'
    case 'credit_card':
      return 'Credit card'
    case 'cash':
      return 'Cash'
    case 'tracking':
      return 'Tracking'
    case 'investment':
      return 'Investment'
    case 'mortgage':
      return 'Mortgage'
    case 'loan':
      return 'Loan'
    case 'other_asset':
      return 'Other asset'
    case 'other_liability':
      return 'Other liability'
  }
}
